// Angular import
import { Component, OnInit, inject, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Subject, Subscription, timer } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';

// project import
import { AuthenticationService } from 'src/app/theme/shared/service/authentication.service';
import { BerryConfig } from 'src/app/app-config';
import { SharedModule } from 'src/app/theme/shared/shared.module';
import { NotificationDropdownComponent } from 'src/app/shared/notification-dropdown/notification-dropdown.component';
import { NotificationService } from 'src/app/shared/notification-dropdown/notification.service';
import { LocationPickerComponent } from 'src/app/shared/location-picker/location-picker.component';
import { LocationService, LocationState } from 'src/app/shared/services/location.service';
import { ChatTabService } from 'src/app/demo/application/chat/chat-tab.service';
import { ChatService } from 'src/app/demo/application/chat/chat.service';
import { SupportSocketService } from 'src/app/demo/application/chat/support-socket.service';

// third party
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import screenfull from 'screenfull';
import { ThemeService } from 'src/app/theme/shared/service/theme.service';

@Component({
  selector: 'app-nav-right',
  imports: [SharedModule, RouterModule, NotificationDropdownComponent, LocationPickerComponent],
  templateUrl: './nav-right.component.html',
  styleUrl: './nav-right.component.scss'
})
export class NavRightComponent implements OnInit, OnDestroy {
  authenticationService = inject(AuthenticationService);
  private translate     = inject(TranslateService);
  private locationSvc   = inject(LocationService);
  private chatService   = inject(ChatService);
  private socketService = inject(SupportSocketService);
  private cdr           = inject(ChangeDetectorRef);

  pickerOpen = false;
  loc: LocationState = this.locationSvc.snapshot;
  private locSub!: Subscription;
  private destroy$ = new Subject<void>();

  chatUnreadCount = 0;

  user?: null;
  screenFull: boolean = true;
  currentView: 'dashboard' | 'admin' = 'dashboard';
  currentViewLabel = 'Dashboard';

  private router = inject(Router);

  private chatTabService = inject(ChatTabService);
  private notifSvc      = inject(NotificationService);
  private toastr        = inject(ToastrService);

  notifUnreadCount = 0;

  onNotifDropdown(isOpen: boolean): void {
    if (isOpen) this.notifSvc.triggerRefresh();
  }

  constructor(
    private themeService: ThemeService
  ) {}

  // Default landing route for each view
  private readonly viewRoutes: Record<'dashboard' | 'admin', string> = {
    dashboard: '/dashboard',
    admin: '/dashboard'
  };

  switchView(view: 'dashboard' | 'admin', label: string) {
    this.themeService.setView(view);
    this.currentView = view;
    this.currentViewLabel = label;
    this.router.navigate([this.viewRoutes[view]]);
  }

  ngOnInit() {
    setTimeout(() => {
      this.translate.setDefaultLang(BerryConfig.i18n);
    }, 0);
    if (screenfull.isEnabled) {
      this.screenFull = screenfull.isFullscreen;
      screenfull.on('change', () => {
        this.screenFull = screenfull.isFullscreen;
      });
    }
    this.locSub = this.locationSvc.location$.subscribe(l => (this.loc = l));

    this.notifSvc.unreadCount$.pipe(takeUntil(this.destroy$)).subscribe(n => {
      this.notifUnreadCount = n;
      this.cdr.detectChanges();
    });

    // Ensure socket is connected so chatNew$ fires globally
    this.socketService.connect();

    // Poll unread summary every 2 minutes, fire immediately on start
    timer(0, 120_000).pipe(
      takeUntil(this.destroy$),
      switchMap(() => this.chatService.getUnreadSummary())
    ).subscribe({
      next: (summary) => {
        this.chatUnreadCount = summary.totalChats;
        this.cdr.detectChanges();
      },
      error: () => {}
    });

    // Increment badge instantly when a new chat arrives via socket
    this.socketService.chatNew$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.chatUnreadCount++;
      this.cdr.detectChanges();
    });

    // A new message on an existing chat can flip it to unread — re-sync the badge
    this.socketService.messageNew$.pipe(
      takeUntil(this.destroy$),
      switchMap(() => this.chatService.getUnreadSummary())
    ).subscribe({
      next: (summary) => {
        this.chatUnreadCount = summary.totalChats;
        this.cdr.detectChanges();
      },
      error: () => {}
    });

    // Decrement badge when a chat is marked as read
    this.socketService.read$.pipe(
      takeUntil(this.destroy$),
      switchMap(() => this.chatService.getUnreadSummary())
    ).subscribe({
      next: (summary) => {
        this.chatUnreadCount = summary.totalChats;
        this.cdr.detectChanges();
      },
      error: () => {}
    });

    // New admin notification via socket
    this.socketService.notificationNew$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      if (data?.type !== 'ADMIN') return;
      this.notifUnreadCount++;
      this.notifSvc.setUnreadCount(this.notifUnreadCount);
      this.notifSvc.pushNewNotif(data);
      this.toastr.info(data.body, data.title, { timeOut: 5000, progressBar: true });
      this.cdr.detectChanges();
    });

    // Play alarm sound when a new trip order is created (status = pending)
    this.socketService.tripStatusUpdate$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      if (data.status === 'pending') {
        this.playNewOrderSound();
      }
    });
  }

  private playNewOrderSound(): void {
    try {
      const audio = new Audio('assets/sound/alarm-buzzer.mp3');
      audio.volume = 0.8;
      audio.play().catch(() => {
        // Browser may block autoplay before user interaction — silently ignore
      });
    } catch {
      // Ignore any Audio API errors
    }
  }

  ngOnDestroy() {
    if (screenfull.isEnabled) {
      screenfull.off('change', () => {
        this.screenFull = screenfull.isFullscreen;
      });
    }
    this.locSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  openChatNotes() {
    this.chatTabService.requestTab('notes');
    this.router.navigate(['/chat']);
  }

  // user logout
  logout() {
    this.authenticationService.logout();
  }

  // user according language change of sidebar menu item
  useLanguage(language: string) {
    this.translate.use(language);
  }

  // public props
  componentSections = [
    {
      title: 'UI Components',
      items: [
        {
          title: 'Alerts',
          url: '/basic/alert'
        },
        {
          title: 'Accordions',
          url: '/basic/collapse'
        },
        {
          title: 'DropDown',
          url: '/basic/dropdowns'
        },
        {
          title: 'Badges',
          url: '/basic/badges'
        },
        {
          title: 'Breadcrumbs',
          url: '/basic/breadcrumb'
        }
      ]
    },
    {
      title: 'Application',
      items: [
        {
          title: 'Chat',
          url: '/chat'
        },
        {
          title: 'Kanban',
          url: '/kanban'
        },
        {
          title: 'Mail',
          url: '/mail'
        },
        {
          title: 'Calendar',
          url: '/calender'
        },
        {
          title: 'E-Commerce',
          url: '/ec/ec-product'
        }
      ]
    },
    {
      title: 'Components',
      items: [
        {
          title: 'Sweet Alert',
          url: '/advance/sweetAlert'
        },
        {
          title: 'Light Box',
          url: '/advance/lightbox'
        },
        {
          title: 'Modal',
          url: '/advance/modal'
        },
        {
          title: 'Notification',
          url: '/advance/notification'
        },
        {
          title: 'Tree View',
          url: '/advance/treeView'
        }
      ]
    }
  ];

  // full screen toggle
  toggleFullscreen() {
    this.screenFull = screenfull.isFullscreen;
    if (screenfull.isEnabled) {
      screenfull.toggle();
    }
  }
}
